
// src/AddTopicForm.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";

const NEW_TOPIC_VALUE = "__NEW_TOPIC__";

function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const AddTopicForm = () => {
  const [visible, setVisible] = useState(false);

  // topics: [{id, title, description, questions: []}]
  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);

  // selection / new topic
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [newTopicName, setNewTopicName] = useState("");

  // question fields
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [description, setDescription] = useState("");

  const [status, setStatus] = useState("");

  // fetch the topics when form is opened
  useEffect(() => {
    if (!visible) return;
    const load = async () => {
      try {
        setLoadingTopics(true);
        const colRef = collection(db, "interview");
        const snap = await getDocs(colRef);
        const items = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            title: data.title || d.id.toUpperCase(),
            description: data.description || "",
            questions: Array.isArray(data.questions) ? data.questions : [],
          };
        });
        items.sort((a, b) =>
          a.title.toLowerCase().localeCompare(b.title.toLowerCase())
        );
        setTopics(items);

        // default selection: none
        setSelectedTopicId("");
        setNewTopicName("");
      } catch (err) {
        console.error("Error fetching topics:", err);
        setStatus("Error: could not load topics from Firestore.");
      } finally {
        setLoadingTopics(false);
      }
    };
    load();
  }, [visible]);

  const selectedTopic = useMemo(
    () => topics.find((t) => t.id === selectedTopicId) || null,
    [topics, selectedTopicId]
  );

  const resetForm = () => {
    setQuestion("");
    setAnswer("");
    setDescription("");
  };

  const createTopic = async () => {
    const name = newTopicName.trim();
    if (!name) {
      setStatus("Please enter a topic name.");
      return;
    }
    const id = slugify(name);
    if (!id) {
      setStatus("Topic name produced an empty slug. Try a different name.");
      return;
    }

    // If topic already exists, auto-select it
    const exists = topics.some((t) => t.id === id);
    if (exists) {
      setSelectedTopicId(id);
      setStatus(`Topic "${name}" already exists. Selected it.`);
      return;
    }

    try {
      setStatus("Creating topic...");
      const topicRef = doc(collection(db, "interview"), id);
      const newDoc = {
        title: name,
        description: "",
        questions: [],
        totalQuestions: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await setDoc(topicRef, newDoc, { merge: false });

      // update local list immediately
      const newTopic = {
        id,
        title: name,
        description: "",
        questions: [],
      };
      setTopics((prev) => [...prev, newTopic].sort((a, b) =>
        a.title.toLowerCase().localeCompare(b.title.toLowerCase())
      ));
      setSelectedTopicId(id);
      setStatus(`Topic "${name}" added successfully.`);
    } catch (err) {
      console.error("Error creating topic:", err);
      setStatus("Error: could not create topic in Firestore.");
    }
  };

  const addQuestionToSelectedTopic = async () => {
    if (!selectedTopicId || selectedTopicId === NEW_TOPIC_VALUE) {
      setStatus("Please select a topic or create the new topic first.");
      return;
    }
    if (!question.trim()) {
      setStatus("Question is required.");
      return;
    }
    // Answer or Description can be empty; still allow save.
    const topic = topics.find((t) => t.id === selectedTopicId);
    if (!topic) {
      setStatus("Selected topic not found.");
      return;
    }

    const newQ = {
      id: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : String(Date.now()),
      topic: topic.title,
      question: question.trim(),
      answer: answer.trim(),
      // a short description for the question (optional field if you use it)
      description: description.trim(),
      order: (topic.questions?.length || 0) + 1,
    };

    const updatedQuestions = [...(topic.questions || []), newQ];
    try {
      setStatus("Adding question...");
      const topicRef = doc(collection(db, "interview"), selectedTopicId);
      await setDoc(
        topicRef,
        {
          title: topic.title, // keep title in sync
          questions: updatedQuestions,
          totalQuestions: updatedQuestions.length,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      // reflect locally
      setTopics((prev) =>
        prev.map((t) =>
          t.id === selectedTopicId ? { ...t, questions: updatedQuestions } : t
        )
      );
      setStatus("Question added successfully.");
      resetForm();
    } catch (err) {
      console.error("Error adding question:", err);
      setStatus("Error: could not add question to Firestore.");
    }
  };

  return (
    <div className="my-3">
      {!visible ? (
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => setVisible(true)}
        >
          + Add Topic / Question
        </button>
      ) : (
        <div className="col-md-10">
        <div className="card shadow-sm">
          <div className="card-body ">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Add Topic / Add Question</h5>
              <button
                className="btn btn-light btn-sm"
                onClick={() => {
                  setVisible(false);
                  setStatus("");
                }}
              >
                Close
              </button>
            </div>

            {/* Select existing topic or choose "Add new" */}
            <div className="mb-3">
              <label className="form-label">Select Topic</label>
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  value={selectedTopicId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedTopicId(v);
                    setStatus("");
                  }}
                  disabled={loadingTopics}
                >
                  <option value="">-- Choose --</option>
                  {topics.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                  <option value={NEW_TOPIC_VALUE}>➕ Add new topic…</option>
                </select>
                {loadingTopics && (
                  <span className="badge text-bg-secondary">Loading…</span>
                )}
              </div>
            </div>

            {/* New topic name input (only when "Add new topic…" is selected) */}
            {selectedTopicId === NEW_TOPIC_VALUE && (
              <div className="mb-3">
                <label className="form-label">New Topic Name</label>
                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., React, Angular, NodeJS"
                    value={newTopicName}
                    onChange={(e) => setNewTopicName(e.target.value)}
                  />
                  <button className="btn btn-success" onClick={createTopic}>
                    Create Topic
                  </button>
                </div>
                <small className="text-muted">
                  The topic will be saved instantly to Firestore and appear in the dropdown.
                </small>
              </div>
            )}

            {/* Question/Answer fields (only for a concrete topic id) */}
            {selectedTopic && (
              <>
                <div className="alert alert-info py-2">
                  <strong>Selected:</strong> {selectedTopic.title}
                </div>

                <div className="mb-3">
                  <label className="form-label">Question</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter question"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Answer</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    placeholder="Enter answer"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                  />
                </div>

                {/* <div className="mb-3">
                  <label className="form-label">Description (optional)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Add a short description or context for this question"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div> */}

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={addQuestionToSelectedTopic}
                  >
                    Add Question
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Clear
                  </button>
                </div>
              </>
            )}

            {/* Status line */}
            {status && (
              <div className="mt-3">
                <small className="text-muted">{status}</small>
              </div>
            )}
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default AddTopicForm;
